import React, { useState, useEffect } from "react";
import {
  Menu,
  X,
  Globe,
  User,
  ScanQrCode,
  Mails,
  ChartNoAxesGantt,
  Container,
  Ship
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { TextShimmer } from "./ui/text-shimmer";
import { LanguageSwitchButton, languages } from "./ui/LanguageSwitchButton";
import { BadgeNew } from "./ui/badge_new";
import { useSurfaceToneForFixedButton } from "./ui/menu_helper";
import { useActiveSection } from "@/hooks/useActiveSection";
import { SECTION_COLOR } from "@/lib/sections";
import { ThemeToggle } from "./ThemeToggle";
import { useTheme } from "./theme-provider";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const activeSection = useActiveSection();

  const prefersReduced =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = () => setIsMenuOpen((prev) => !prev);

  const currentLangIndex = languages.findIndex(
    (l) => l.code === i18n.language
  );
  const safeLangIndex = currentLangIndex >= 0 ? currentLangIndex : 0;

  const handleLangChange = () => {
    const nextIndex = (safeLangIndex + 1) % languages.length;
    i18n.changeLanguage(languages[nextIndex].code);
    setOpen(false);
  };

  const navLinks = [
    { name: t("navigation.about"), href: "#about", icon: User },
    { name: t("navigation.marine"), href: "#marine", icon: Ship },
    { name: t("navigation.containers"), href: "#containers", icon: Container },
    { name: t("navigation.scan"), href: "#scan", icon: ScanQrCode },
    { name: t("navigation.projects"), href: "#projects", icon: ChartNoAxesGantt },
    { name: t("navigation.contact"), href: "#contact", icon: Mails },
  ];

  const badgeLinks = [3];

  const fixedBtnRef = React.useRef<HTMLButtonElement>(null);
  const tone = useSurfaceToneForFixedButton(fixedBtnRef);
  const { theme } = useTheme();

  // The floating button uses the tone from the background (scroll-dependent)
  const isPillDark = tone === "dark";

  // The menu itself should directly reflect the current selected theme or global system theme
  const isDark = theme === "dark" || (theme === "system" && typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches);


  // płynne przewijanie po kliknięciu
  const scrollToHash = (hash: string) => {
    const id = hash.replace("#", "");
    const target = document.getElementById(id);
    if (!target) return;
    const prefersReduced =
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    target.scrollIntoView({
      behavior: prefersReduced ? "auto" : "smooth",
      block: "start",
    });
  };

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
    href: string
  ) => {
    e.preventDefault();
    setIsMenuOpen(false);

    const idx = href.indexOf("#");
    const hash = idx >= 0 ? href.slice(idx) : "";
    if (hash) {
      requestAnimationFrame(() => scrollToHash(hash));
    }
  };

  // style wspólne dla linków
  const mobileLinkClass = cn(
    "transition-colors flex items-center gap-3",
    isDark
      ? "text-foreground hover:text-blue-300"
      : "text-slate-900 hover:text-slate-700"
  );
  const mobileIconClass = cn(
    "w-5 h-5",
    isDark ? "text-blue-200" : "text-blue-600/60"
  );

  return (
    <>
      <header
        className={cn(
          "w-full z-50 transition-all duration-300",
          isScrolled ? "shadow-md" : "border-b border-white/10",
          "bg-navy py-3",
          "md:fixed md:top-0 md:left-0"
        )}
      >
        <div className="container mx-auto flex items-center justify-between">
          <a href="#home" className="active-drop flex items-center">
            <img src="/logo.png" className="h-10 w-auto mr-2" alt="REMAR Engineering — logo firmy" />
            <div className="flex flex-col">
              <TextShimmer
                duration={1.2}
                delay={3}
                className="text-4xl font-bold"
              >
                REMAR
              </TextShimmer>
              <div className="text-blue-500 font-bold text-[12px]">
                {(activeSection === "scan" || activeSection === "projects" || activeSection === "containers")
                  ? <span className="tracking-[0.38em] font-black drop-shadow-[0_0_8px_rgba(59,130,246,0.6)]">ENGINEERING</span>
                  : "REPAIRS&OUTFITTING"}
              </div>
            </div>
          </a>

          {/* DESKTOP NAV */}
          <nav className="relative hidden md:flex items-center gap-4 lg:gap-6 xl:gap-8">
            {navLinks.map((link, i) => {
              const sectionId = link.href.replace("#", "");
              const isActive = activeSection === sectionId;
              const linkColor = SECTION_COLOR[sectionId] ?? "#3B82F6";

              const linkInner = (
                <a
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="relative text-white/80 hover:text-white transition-colors py-1"
                  style={isActive ? { color: linkColor } : undefined}
                >
                  {link.name}
                  {/* Glow underline */}
                  <AnimatePresence>
                    {isActive && (
                      <motion.span
                        layoutId="nav-glow"
                        key="nav-glow"
                        className="absolute bottom-0 left-1/2 -translate-x-1/2 rounded-full pointer-events-none"
                        style={{
                          width: "80%",
                          height: "3px",
                          background: linkColor,
                          filter: prefersReduced ? "none" : "blur(4px)",
                          opacity: 0.75,
                        }}
                        initial={{ opacity: 0, scaleX: 0 }}
                        animate={{ opacity: 0.75, scaleX: 1 }}
                        exit={{ opacity: 0, scaleX: 0 }}
                        transition={prefersReduced ? { duration: 0 } : { duration: 0.3, ease: "easeOut" }}
                      />
                    )}
                  </AnimatePresence>
                </a>
              );

              return badgeLinks.includes(i) ? (
                <BadgeNew
                  key={link.name}
                  label="NEW!"
                  variant="blue"
                  animation="bounce"
                >
                  {linkInner}
                </BadgeNew>
              ) : (
                <React.Fragment key={link.name}>{linkInner}</React.Fragment>
              );
            })}
            <LanguageSwitchButton className="text-white/80 hover:text-white" />
            <ThemeToggle className="text-white/80" />
          </nav>

          {/* Przycisk menu (mobile) */}
          <button
            className="hidden text-foreground p-3 rounded-full hover:bg-navy-light transition-colors"
            onClick={toggleMenu}
            aria-label={isMenuOpen ? t("global.close") : t("global.menu")}
            aria-controls="mobile-nav"
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Overlay */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMenuOpen(false)}
              className={cn(
                "fixed inset-0 z-[60] backdrop-blur-[4px] md:hidden",
                isDark ? "bg-black/60" : "bg-black/40"
              )}
              aria-hidden="true"
            />
          )}
        </AnimatePresence>

        {/* BOTTOM SHEET */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              id="mobile-nav"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className={cn(
                "md:hidden fixed bottom-0 left-0 right-0 z-[65] rounded-t-3xl overflow-hidden shadow-2xl ring-1 flex flex-col max-h-[85vh]",
                isDark
                  ? "bg-slate-900/95 ring-white/10 text-foreground"
                  : "bg-white/95 ring-black/5 text-slate-900",
                "backdrop-blur-xl"
              )}
            >
              {/* Drag Handle & Header */}
              <div className="flex flex-col items-center pt-3 pb-2 px-6 relative w-full">
                <div className={cn("w-12 h-1.5 rounded-full mb-4", isDark ? "bg-white/20" : "bg-black/20")} />
                <div className="w-full flex items-center justify-between">
                  <span className="text-xl font-bold tracking-tight">{t("global.menu") || "Menu"}</span>
                  <button
                    onClick={() => setIsMenuOpen(false)}
                    className={cn(
                      "p-2 rounded-full transition-colors",
                      isDark ? "bg-white/10 hover:bg-white/20" : "bg-black/5 hover:bg-black/10"
                    )}
                    aria-label={t("global.close")}
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Scrollable Content */}
              <div className="overflow-y-auto px-6 pb-12 custom-scrollbar">
                {/* 2-Column Grid for Nav Links */}
                <div className="grid grid-cols-2 gap-3 mt-4 mb-6">
                  {navLinks.map((link, i) => {
                    const Icon = link.icon;
                    const sectionId = link.href.replace("#", "");
                    const isActive = activeSection === sectionId;
                    const linkColor = SECTION_COLOR[sectionId] ?? "#3B82F6";

                    const linkContent = (
                      <a
                        href={link.href}
                        onClick={(e) => handleNavClick(e, link.href)}
                        className={cn(
                          "flex flex-col items-center justify-center gap-3 p-4 rounded-2xl transition-all duration-200 active:scale-95 text-center h-full",
                          isDark
                            ? isActive ? "bg-white/10 ring-1 ring-white/20" : "bg-white/5 hover:bg-white/10"
                            : isActive ? "bg-black/5 ring-1 ring-black/10" : "bg-black/[0.03] hover:bg-black/[0.06]"
                        )}
                        style={isActive ? { color: linkColor } : {}}
                      >
                        <Icon
                          size={28}
                          className={isActive ? "" : isDark ? "text-slate-300" : "text-slate-600"}
                          style={isActive ? { color: linkColor } : {}}
                        />
                        <span className={cn("font-medium text-[13px]", isActive ? "" : isDark ? "text-slate-300" : "text-slate-700")}>
                          {link.name}
                        </span>
                      </a>
                    );

                    return badgeLinks.includes(i) ? (
                      <BadgeNew key={link.name} label="NEW!" variant="blue" animation="bounce" className="block w-full h-full">
                        {linkContent}
                      </BadgeNew>
                    ) : (
                      <React.Fragment key={link.name}>{linkContent}</React.Fragment>
                    );
                  })}
                </div>

                {/* Footer Controls (Language / Theme) */}
                <div className={cn(
                  "flex items-center justify-between p-4 rounded-2xl",
                  isDark ? "bg-white/5" : "bg-black/[0.03]"
                )}>
                  <button
                    onClick={handleLangChange}
                    className="flex items-center gap-3 font-medium transition-opacity hover:opacity-80"
                  >
                    <div className={cn(
                      "p-2 rounded-full",
                      isDark ? "bg-white/10" : "bg-white shadow-sm"
                    )}>
                      <Globe size={18} />
                    </div>
                    <span>{languages[safeLangIndex].label}</span>
                  </button>
                  <ThemeToggle />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Floating Bottom Pill Button */}
      <AnimatePresence>
        {!isMenuOpen && (
          <motion.div
            initial={{ y: 100, opacity: 0, x: "-50%" }}
            animate={{ y: 0, opacity: 1, x: "-50%" }}
            exit={{ y: 100, opacity: 0, x: "-50%" }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            className="fixed bottom-6 left-1/2 z-[55] md:hidden"
          >
            <button
              ref={fixedBtnRef}
              data-tone={tone}
              className={cn(
                "flex items-center gap-2 px-6 py-3.5 rounded-full shadow-2xl ring-1 backdrop-blur-xl transition-all duration-300 active:scale-95",
                isPillDark
                  ? "bg-foreground/15 ring-foreground/20 text-foreground hover:bg-foreground/20"
                  : "bg-white/90 ring-black/10 text-slate-900 hover:bg-white"
              )}
              onClick={toggleMenu}
              aria-label={t("global.menu")}
              aria-controls="mobile-nav"
              aria-expanded={isMenuOpen}
            >
              <Menu size={22} />
              <span className="font-semibold text-[15px] mr-1">{t("global.menu") || "Menu"}</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
